import type { CSSProperties } from "react";

import { AppVersion, TiendaNubeLogo } from "./BrandHeader";

type SkeletonProps = {
  width?: number | string;
  height?: number | string;
  radius?: number | string;
  inline?: boolean;
  style?: CSSProperties;
  className?: string;
};

export function Skeleton({
  width = "100%",
  height = 12,
  radius = 6,
  inline = false,
  style,
  className = "",
}: SkeletonProps) {
  return (
    <span
      className={`skel ${className}`}
      style={{
        width,
        height,
        borderRadius: radius,
        display: inline ? "inline-block" : "block",
        ...style,
      }}
      aria-hidden="true"
    />
  );
}

export function SkeletonText({ lines = 3 }: { lines?: number }) {
  return (
    <div className="skelText" aria-hidden="true">
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          height={12}
          width={i === lines - 1 ? "62%" : "100%"}
        />
      ))}
    </div>
  );
}

export function SkeletonAvatar({ size = 56 }: { size?: number }) {
  return <Skeleton width={size} height={size} radius="50%" />;
}

/**
 * DashboardSkeleton — replaces the old FullPageState splash for the bootstrap
 * fetch. Renders the live page shape (hero · roster · sections) with
 * shimmering placeholders so the layout doesn't jump when data lands.
 *
 * TiendaNube/Nimbus convention: every loading screen mirrors the live page
 * shape, never a centered spinner.
 */
export function DashboardSkeleton() {
  return (
    <main className="dashboard" aria-busy="true" aria-label="Cargando tablero">
      <section className="heroEditorial heroEditorial--skeleton">
        <div className="heroEditorial__topRow">
          <span className="heroEditorial__eyebrow">
            <TiendaNubeLogo size={14} />
            <span>Agentes Nube</span>
            <AppVersion />
          </span>
          <Skeleton width={120} height={12} inline />
        </div>
        <Skeleton height={32} width="56%" radius={8} />
        <div className="heroEditorial__stats">
          <Skeleton width={90} height={16} radius={8} />
          <Skeleton width={110} height={16} radius={8} />
          <Skeleton width={80} height={16} radius={8} />
        </div>
        <Skeleton width={180} height={36} radius={8} />
      </section>

      <section className="sectionEditorial">
        <div className="sectionEditorial__header">
          <Skeleton width={140} height={22} radius={6} />
          <Skeleton width={180} height={13} radius={6} />
        </div>
        <div className="rosterGrid">
          {[0, 1, 2].map((i) => (
            <article key={i} className="agentRosterCard agentRosterCard--skeleton">
              <div className="agentRosterCard__top">
                <SkeletonAvatar size={56} />
                <div className="agentRosterCard__identity" style={{ flex: 1 }}>
                  <Skeleton height={18} width="62%" />
                  <Skeleton height={12} width="42%" />
                </div>
              </div>
              <SkeletonText lines={2} />
              <Skeleton height={28} width="80%" radius={8} />
              <div className="agentRosterCard__ctas">
                <Skeleton height={32} radius={8} />
                <Skeleton height={32} radius={8} />
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="sectionEditorial">
        <div className="sectionEditorial__header">
          <Skeleton width={120} height={22} radius={6} />
        </div>
        <div className="stack" style={{ gap: 12 }}>
          {[0, 1].map((i) => (
            <article key={i} className="approvalCard" style={{ padding: 16 }}>
              <Skeleton width="40%" height={14} />
              <Skeleton width="80%" height={18} style={{ marginTop: 8 }} />
              <SkeletonText lines={2} />
            </article>
          ))}
        </div>
      </section>
    </main>
  );
}

/**
 * SectionSkeleton — generic placeholder for tab content (Profile, Historial,
 * Hire detail).
 */
export function SectionSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <section aria-busy="true" className="sectionSkeleton">
      <article className="hireSection">
        <Skeleton width="35%" height={20} radius={6} />
        <div style={{ marginTop: 12 }}>
          <SkeletonText lines={rows} />
        </div>
      </article>
      <article className="hireSection" style={{ marginTop: 16 }}>
        <Skeleton width="28%" height={20} radius={6} />
        <div style={{ marginTop: 12 }}>
          <SkeletonText lines={rows} />
        </div>
      </article>
    </section>
  );
}

